<template>
  <div class="vue-flow__node-default custom-node-padding action-node" :class="nodeClass">
    <Handle type="target" :position="Position.Left" />
    <div class="node-type-tag">{{ actionTypeLabel }}</div>
    <div>{{ data.label !== undefined && data.label !== null && data.label !== '' ? data.label : 'Action Step' }}</div>
    <div v-for="item in summaryItems" :key="item.id" class="node-description">
      <div class="node-description-label">{{ item.label }}</div>
      <div class="node-description-value" :class="{ 'node-description-url': item.id === 'url' }">{{ item.value }}</div>
    </div>
    <Handle v-if="showSourceHandle === true" type="source" :position="Position.Right" />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { Handle, Position } from '@vue-flow/core';

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
});

const actionTypeLabel = computed(() => {
  if (props.data !== null && props.data !== undefined && props.data.actionType === 'transferCall') {
    return 'Transfer Call';
  }
  if (props.data !== null && props.data !== undefined && props.data.actionType === 'endCall') {
    return 'End Call';
  }
  if (props.data !== null && props.data !== undefined && props.data.actionType === 'announce') {
    return 'Announce';
  }
  if (props.data !== null && props.data !== undefined && props.data.actionType === 'httpRequest') {
    return 'HTTP Request';
  }
  if (props.data !== null && props.data !== undefined && props.data.toolSource === 'inlineJsTool') {
    return 'JS Snippet';
  }
  return 'Action Step';
});

const nodeClass = computed(() => {
  if (props.data !== null && props.data !== undefined && props.data.actionType === 'endCall') {
    return 'end-call-node';
  }
  if (props.data !== null && props.data !== undefined && props.data.actionType === 'transferCall') {
    return 'transfer-call-node';
  }
  return '';
});

const showSourceHandle = computed(() => {
  if (props.data !== null && props.data !== undefined && props.data.actionType === 'endCall') {
    return false;
  }
  return true;
});

function trimText(value) {
  if (typeof value === 'string') {
    return value.trim();
  }
  return '';
}

function shortenText(value) {
  const text = trimText(value);
  if (text.length <= 90) {
    return text;
  }
  return `${text.slice(0, 87)}...`;
}

function destinationHasPhoneIpPort(destination) {
  const value = trimText(destination);
  if (value.length === 0) {
    return false;
  }
  const atIndex = value.indexOf('@');
  const phoneCandidate = atIndex > 0 ? value.slice(0, atIndex) : value;
  const phoneDigits = phoneCandidate.replace(/\D/g, '');
  if (phoneDigits.length < 7) {
    return false;
  }
  if (/\d{1,3}[.]\d{1,3}[.]\d{1,3}[.]\d{1,3}/.test(value) !== true) {
    return false;
  }
  if (/:\d{2,5}\b/.test(value) !== true) {
    return false;
  }
  return true;
}

const detailsText = computed(() => {
  if (props.data !== null && props.data !== undefined && props.data.actionType === 'httpRequest') {
    const method = props.data.config !== undefined && props.data.config !== null && typeof props.data.config.method === 'string' ? props.data.config.method : 'POST';
    const url = props.data.config !== undefined && props.data.config !== null && typeof props.data.config.url === 'string' ? props.data.config.url : '';
    if (url.trim().length > 0) {
      return `${method} ${url}`;
    }
    return method;
  }
  if (props.data !== null && props.data !== undefined && props.data.actionType === 'transferCall') {
    const destination = props.data.config !== undefined && props.data.config !== null && typeof props.data.config.destination === 'string' ? props.data.config.destination.trim() : '';
    if (destinationHasPhoneIpPort(destination) === true) {
      return `Transfer to ${destination}`;
    }
    return '';
  }
  if (props.data !== null && props.data !== undefined && props.data.actionType === 'endCall') {
    return '';
  }
  if (props.data !== null && props.data !== undefined && props.data.actionType === 'announce') {
    return props.data.config !== undefined && props.data.config !== null && props.data.config.allowUserInterrupt === false ? 'Locked while speaking' : 'User can stop by talking';
  }
  if (props.data !== null && props.data !== undefined && props.data.toolSource === 'inlineJsTool') {
    const snippetName = props.data.inlineTool !== undefined && props.data.inlineTool !== null ? trimText(props.data.inlineTool.toolName) : '';
    if (snippetName.length > 0) {
      return `Run ${snippetName}`;
    }
    return 'Run JavaScript';
  }
  if (props.data !== null && props.data !== undefined && props.data.selectedToolId !== undefined && props.data.selectedToolId !== null && String(props.data.selectedToolId).length > 0) {
    return `Tool: ${props.data.selectedToolId}`;
  }
  return '';
});

const descriptionText = computed(() => {
  if (props.data === null || props.data === undefined) {
    return '';
  }
  return shortenText(props.data.stepDescription);
});

const messageText = computed(() => {
  if (props.data === null || props.data === undefined) {
    return '';
  }
  return shortenText(props.data.message);
});

const summaryItems = computed(() => {
  const items = [];
  if (props.data !== null && props.data !== undefined && props.data.actionType === 'transferCall') {
    if (detailsText.value.length > 0) {
      items.push({ id: 'transfer', label: 'Transfer', value: detailsText.value });
    }
    return items;
  }
  if (props.data !== null && props.data !== undefined && props.data.actionType === 'endCall') {
    if (messageText.value.length > 0) {
      items.push({ id: 'message', label: 'Message Before Hanging', value: messageText.value });
    }
    if (descriptionText.value.length > 0) {
      items.push({ id: 'description', label: 'When', value: descriptionText.value });
    }
    return items;
  }
  if (props.data !== null && props.data !== undefined && props.data.actionType === 'announce') {
    if (messageText.value.length > 0) {
      items.push({ id: 'message', label: 'Announcement', value: messageText.value });
    }
    if (detailsText.value.length > 0) {
      items.push({ id: 'interrupt', label: 'Interrupt', value: detailsText.value });
    }
    return items;
  }
  if (props.data !== null && props.data !== undefined && props.data.actionType === 'httpRequest') {
    const method = props.data.config !== undefined && props.data.config !== null && typeof props.data.config.method === 'string' ? props.data.config.method : 'POST';
    const url = props.data.config !== undefined && props.data.config !== null && typeof props.data.config.url === 'string' ? props.data.config.url.trim() : '';
    if (descriptionText.value.length > 0) {
      items.push({ id: 'description', label: 'Description', value: descriptionText.value });
    }
    if (messageText.value.length > 0) {
      items.push({ id: 'message', label: 'Message', value: messageText.value });
    }
    items.push({ id: 'method', label: 'Method', value: method });
    if (url.length > 0) {
      items.push({ id: 'url', label: 'URL', value: url });
    }
    return items;
  }
  if (descriptionText.value.length > 0) {
    items.push({ id: 'description', label: 'Description', value: descriptionText.value });
  }
  if (messageText.value.length > 0) {
    items.push({ id: 'message', label: 'Message', value: messageText.value });
  }
  if (detailsText.value.length > 0) {
    items.push({ id: 'action', label: 'Action', value: detailsText.value });
  }
  return items;
});
</script>

<style scoped>
.action-node {
  background-color: #F9FAFB;
  border: 1px solid #D1D5DB;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  min-width: 260px;
  max-width: 380px;
}
.transfer-call-node {
  border-color: #F59E0B;
  background-color: #FFFBEB;
}
.transfer-call-node .node-type-tag {
  background-color: #B45309;
}
.end-call-node {
  border-color: #DC2626;
  background-color: #FEF2F2;
}
.end-call-node .node-type-tag {
  background-color: #DC2626;
}
.node-type-tag {
  background-color: #6B7280;
  color: #FFFFFF;
  font-size: 0.75rem;
  padding: 4px 12px;
  border-radius: 9999px;
  margin-bottom: 10px;
  display: inline-block;
  text-transform: uppercase;
  font-weight: 600;
  border: none;
}
.custom-node-padding {
  padding: 15px 20px;
}
.action-node > div:not(.node-type-tag):not(.node-description) {
  font-size: 0.9rem;
  color: #334155;
  text-align: center;
}
.node-description {
  font-size: 0.8rem;
  color: #4B5563;
  margin-top: 8px;
  white-space: pre-wrap;
  background-color: #F3F4F6;
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px dashed #D1D5DB;
  text-align: left;
  min-width: 220px;
  max-width: 100%;
  overflow: hidden;
}
.node-description-value {
  overflow-wrap: anywhere;
  word-break: break-all;
}
.node-description-url {
  direction: ltr;
  unicode-bidi: plaintext;
  text-align: left;
}
.node-description-label {
  font-size: 0.66rem;
  color: #64748B;
  text-transform: uppercase;
  font-weight: 700;
  margin-bottom: 3px;
}
</style> 